import { useDebounceFn } from 'ahooks';
import { Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useAppDispatch } from 'app/hooks';
import { routesMap } from 'app/Router';
import { RevxButton } from 'components/RevxButton';
import { RevxDate } from 'components/RevxDate';
import { RevxInputNumber } from 'components/RevxInputNumber';
import { RevxMoney } from 'components/RevxMoney';
import { JobPriceAdjust, JobRFQQuoteItemViewer } from 'domains/jobconfigDomain';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import workspaceService from 'services/jobService';
import { DEBOUNCE_TIME, MIN_PCT, SHIPPING_WILL_CALL } from 'utils/constants';
import { formatNumberToThousandths } from 'utils/number';

const quoteTableFirstColValueMap = [
  'Supplier',
  'Ready Date',
  'Printing Costs',
  'Profit Margin/Discount',
  'Shipping Method',
  'Shipping Account',
  'Shipment Costs',
  'Shipping Markup/Discount',
  'Job Net Total',
  'footer',
];

type MockData = JobRFQQuoteItemViewer & { colName: string };

type Props = {
  isOwnRFQJob: boolean;
  data?: JobRFQQuoteItemViewer[];
  updateData: (data: JobRFQQuoteItemViewer[]) => void;
  jobId?: number;
};

const TabQuoteBuilder: React.FC<Props> = ({
  isOwnRFQJob,
  data,
  updateData,
  jobId,
}) => {
  const [quoteCol, setQuoteCol] = useState<ColumnsType<MockData>>([]);
  const [mockQuoteData, setMockQuoteData] = useState<Array<MockData>>([]);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  useEffect(() => {
    genQuoteTableCol();
    mockQuoteDataFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  const genQuoteTableCol = () => {
    const len = quoteTableFirstColValueMap.length;
    const lastRowIndex = len - 1;
    const colSpanLen = data!.length + 1;
    console.log('isOwnRFQJob, lastRowIndex, colSpanLen', isOwnRFQJob, lastRowIndex, colSpanLen);
    const firstCol: ColumnsType<MockData> = [
      {
        title: () => <div className="table-title-weight">Supplier</div>,
        dataIndex: 'colName',
        width: 105,
        render: (value, record, index) => {
          const colSpan =
            index === lastRowIndex && !isOwnRFQJob ? colSpanLen : 1;
          const children =
            index === lastRowIndex ? (
              <div style={{ textAlign: 'center' }}>
                {!isOwnRFQJob && (
                  <RevxButton
                    className="add-cart-btn"
                    type="primary"
                    brandOrange
                    onClick={async () => {
                      await dispatch.app.showLoading();
                      if (jobId) {
                        await workspaceService.rfqJobReturnQuote(jobId);
                      }
                      await dispatch.app.hideLoading();
                      dispatch.workspace.reloadJobList();
                    }}
                  >
                    {t('workspace.rfq.btn.returnQuote')}
                  </RevxButton>
                )}
              </div>
            ) : index === len - 2 ? (
              <div className="job-net-total ta-l">{value}</div>
            ) : (
              <div className="ta-l">{value}</div>
            );
          return {
            children,
            props: {
              colSpan,
            },
          };
        },
      },
    ];
    const dynamicCol: ColumnsType<MockData> = [];
    if (data) {
      data.forEach((i, index) => {
        const { quantity } = i;
        dynamicCol.push({
          title: (
            <div>
              <p>Quantity {index + 1}</p>
              <p className="table-title-weight">
                {formatNumberToThousandths(quantity)}
              </p>
            </div>
          ),
          dataIndex: 'quantity',
          render: (value, record, rowIndex) => {
            const tdRender = () => {
              const {
                revxPrintingCost,
                revxPrintingMarkup,
                revxPrintingMarkupPct,
                revxFreightCost,
                revxShippingMarkup,
                revxShippingMarkupPct,
                revxCharge,
                orgName,
                qtySerialNumber,
                orgId,
                shippingInfos,
                readyDate,
              } = data[index];
              const safeShippingInfos = Array.isArray(shippingInfos)
                ? shippingInfos
                : [];
              const markupDisabled = revxFreightCost === 0;
              const formatShippingItem = (type: 'method' | 'account') => {
                if (type === 'method') {
                  if (safeShippingInfos.length > 1) {
                    return t('workspace.rfq.splitShipment');
                  } else if (safeShippingInfos.length === 0) {
                    return <span>N/A</span>;
                  } else {
                    return safeShippingInfos[0]?.carrierService || <span>N/A</span>;
                  }
                } else {
                  if (safeShippingInfos.length > 1) {
                    return t('workspace.list.various');
                  } else if (safeShippingInfos.length === 0) {
                    return <span>N/A</span>;
                  } else {
                    const { accountCompany, carrierService } =
                      safeShippingInfos[0];
                    if (carrierService === SHIPPING_WILL_CALL) {
                      return <span>N/A</span>;
                    }
                    return accountCompany || <span>N/A</span>;
                  }
                }
              };
              switch (rowIndex) {
                case 0:
                  return <div className="supplier-name">{orgName}</div>;
                case 1:
                  return <RevxDate dateStr={readyDate} />;
                case 2:
                  return RevxMoney({ value: revxPrintingCost });
                case 3:
                  return (
                    <div className="markup-input">
                      <RevxInputNumber
                        className="markup-input__pct"
                        suffix="%"
                        value={revxPrintingMarkupPct}
                        min={MIN_PCT}
                        onChange={async (v) => {
                          cancelMarkupChangeFn();
                          await markupChangeFn(qtySerialNumber, orgId, {
                            revxMarkupPct: v,
                          });
                        }}
                      />
                      <RevxInputNumber
                        className="markup-input__price"
                        prefix="$"
                        value={revxPrintingMarkup}
                        precision={2}
                        onChange={async (v) => {
                          cancelMarkupChangeFn();
                          await markupChangeFn(qtySerialNumber, orgId, {
                            revxMarkup: v,
                          });
                        }}
                      />
                    </div>
                  );
                case 4:
                  return <div>{formatShippingItem('method')}</div>;
                case 5:
                  return <div>{formatShippingItem('account')}</div>;
                case 6:
                  return RevxMoney({ value: revxFreightCost });
                case 7:
                  return (
                    <div className="markup-input">
                      <RevxInputNumber
                        disabled={markupDisabled}
                        className="markup-input__pct"
                        suffix="%"
                        value={revxShippingMarkupPct}
                        min={MIN_PCT}
                        onChange={async (v) => {
                          cancelMarkupChangeFn();
                          await markupChangeFn(qtySerialNumber, orgId, {
                            revxShippingMarkupPct: v,
                          });
                        }}
                      />
                      <RevxInputNumber
                        disabled={markupDisabled}
                        className="markup-input__price"
                        prefix="$"
                        value={revxShippingMarkup}
                        onChange={async (v) => {
                          cancelMarkupChangeFn();
                          await markupChangeFn(qtySerialNumber, orgId, {
                            revxShippingMarkup: v,
                          });
                        }}
                      />
                    </div>
                  );
                case 8:
                  return (
                    <div className="job-net-total">
                      {RevxMoney({ value: revxCharge })}
                    </div>
                  );
                case 9:
                  return (
                    <RevxButton
                      className="add-cart-btn"
                      type="primary"
                      brandOrange
                      onClick={async () => {
                        await addToCartHandler(orgId, qtySerialNumber);
                      }}
                    >
                      {t('workspace.list.addToCart')}
                    </RevxButton>
                  );
              }
            };
            return {
              children: <div>{tdRender()}</div>,
              props: {
                colSpan: rowIndex === lastRowIndex && !isOwnRFQJob ? 0 : 1,
              },
            };
          },
        });
      });
    }
    setQuoteCol([...firstCol, ...dynamicCol]);
  };
  const mockQuoteDataFn = () => {
    if (data) {
      setMockQuoteData(
        quoteTableFirstColValueMap.map((colName, key) => {
          return { ...data[0], ...{ colName, key } };
        })
      );
    }
  };
  const addToCartHandler = async (orgId: number, qtySerialNumber: number) => {
    if (!jobId) return;
    await dispatch.app.showLoading();
    try {
      await workspaceService.addRfqJobToCart({
        orgId,
        qtySerialNumber,
        id: jobId,
      });
      await dispatch.app.hideLoading();
      history.push(routesMap.shoppingCart.path);
    } catch {
      await dispatch.app.hideLoading();
    }
  };
  const { run: markupChangeFn, cancel: cancelMarkupChangeFn } = useDebounceFn(
    async (
      qtySerialNumber: number,
      orgId: number,
      data: Partial<JobPriceAdjust>
    ) => {
      await markupChangeHandler(qtySerialNumber, orgId, data);
    },
    { wait: DEBOUNCE_TIME }
  );
  const markupChangeHandler = async (
    qtySerialNumber: number,
    orgId: number,
    data: Partial<JobPriceAdjust>
  ) => {
    if (!jobId) return;
    await dispatch.app.showLoading();
    const markup = await workspaceService.adjustRfqJobSourcerPrice({
      id: jobId,
      qtySerialNumber,
      orgId,
      sendParams: data,
    });
    updateData(markup);
    await dispatch.app.hideLoading();
  };
  return (
    <div className="rfq-quote-table">
      <Table
        dataSource={mockQuoteData}
        columns={quoteCol}
        pagination={false}
        rowKey="key"
      />
    </div>
  );
};

export default TabQuoteBuilder;
